import React, { useEffect, useState } from 'react'
import { BrowserRouter as Router, Route, useHistory, useLocation } from 'react-router-dom'
import { darkTheme, DialogContainer, lightTheme, Provider as V3Provider, ProgressCircle, AlertDialog, Text } from '@adobe/react-spectrum'
import { Provider as ReduxProvider, useSelector, useDispatch } from 'react-redux'
import Provider from '@react/react-spectrum/Provider'
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js'
import { LoginCallback, SecureRoute, Security } from '@okta/okta-react'
import Chat from './Apps/Chat/Chat'
import Phone from './Apps/Phone'
import AllConversations from './Apps/Chat/Modules/AllConversations/AllConversations'
import { NavigationMenu } from './Layout'
import NetworkNotification from './Layout/Notification/NetworkNotification'
import store from './redux/store'
import './Theme/Styles/styles.scss'
import Login from './Layout/Login/Login'
import oktaconfig from './okta-stage-config'
import Profile from './Profile'
import Logout from './Layout/Login/Logout'
import Notification from './Layout/Notification/Notification'
import Offline from './Apps/ContactCenter/Layout/Offline/Offline'
import ContactCenter from './Apps/ContactCenter/Modules/ContactCenter'
import useOnLoad from './hooks/useOnLoad'
import { IncomingCall } from './Apps/ContactCenter/Layout/IncomingCall'
import { MissedCall } from './Apps/ContactCenter/Layout/MissedCall'
import { FailurePopup } from './Apps/ContactCenter/Layout/FailurePopup'
import RemoveParticipant from './Layout/Notification/RemoveParticipant'
import { fetchAgentFeatureFlag } from './Apps/ContactCenter/Api/featureFlag'
import { updateLastAgentStatusOnVpn, setReleaseNotes } from './redux/actions/preferencesActions'
import { setAgentDetails, setAgentFeatureTags, setAuthFail } from './redux/actions/contactCenterActions'
import { fetchTapToolData, getReleaseNotes } from './Apps/ContactCenter/Api/api'
import localization from './Apps/Chat/Components/lang/localization'
import { RootState } from './redux/reducers/rootReducer'
import { checkMockOfferIdEligibility } from './redux/actions/CheckEligibilityActions'
import { updateRTCSEligibilityFlag, updateRTCSContactSentiment, isRTCSEnabledByContactId ,isRTCSContactSentimentCreatedByContactId, isRTCSBOTContactSentimentCreatedByContactId, updateRTCSBotContactSentiment } from './helpers/rtcs-ctr-analytics';
import { getCurrentConvID, getCurrentQueue, getInitialConvId } from './utils/conversation-utils';
import { getOACFeatureFlags } from './utils/fg-utils'
import { vpnConnectionStateHandler } from './helpers/vpn-connection-state-handler'
import VpnValidation from './Apps/Chat/Components/vpn-validation';

declare const window: any;
const AGENT_STATE_VALUES = ['Pending', 'CallingCustomer', 'Missed', 'Busy', 'AfterCallWork', 'Offline', 'PendingBusy'];

const ThemeContainer = () => {
  const location = useLocation()
  const showCCPLoader = ['/chat', '/history']
  const [networkMode, setNetworkMode] = useState('')
  const [isValidCountryCode, setIsValidCountryCode] = useState(true);
  const { darkMode: globalDarkMode, language, isVpnConnected, lastAgentStatusWhenVpnIsOn, pageHistoryPathname, releaseNotes: releaseNoteState, showLoaderOnOAC } = useSelector((state: RootState) => state.preferences)
  const { currentConversation, notification, alertData, CCPStatus, incomingCallData, missedCallData, recordingDisabled, participantLeft, agentDetails, isAuthFail, enableRTS, enabledQueuesRTS, contactRTSentiments, conversationLegSentiments, currentConversationAC, currentView } = useSelector((state: RootState) => state.contactCenter)
  const currContactId = getCurrentConvID();
  const currentQueue = getCurrentQueue();
  const currentQueueACview = currentConversationAC?.ocPlatformData?.chatInfo?.queueName ?? '';
  const queueACViewOrChatView = currentView === 'AC_VIEW' ? currentQueueACview : currentQueue;
  const initialContactId = getInitialConvId();
  const botContactId = initialContactId ? `${initialContactId}-BOT` : '';
  const dispatch = useDispatch()
  const [isOpen, setIsOpen] = useState(false);
  const [releaseNoteOpen, releaseNotePopUp] = useState(false)
  const V3Theme = globalDarkMode ? darkTheme : lightTheme
  const V2Theme = globalDarkMode ? 'dark' : 'light'
  const agentDesktopClass = globalDarkMode ? 'agent-desktop agent-desktop-scroll-dark' : 'agent-desktop agent-desktop-scroll-light'
  const { ldap = '' } = agentDetails

  React.useEffect(() => {
    const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
    const MAX_RETRIES = 3;
  
    async function loadTapToolData() {
      let success = false;
      let maxRetries = MAX_RETRIES;
      let delayTime = 1000; 
  
      while (maxRetries > 0 && !success) {
        try {
          const tapToolResponse: any = await fetchTapToolData(); 
          window.tapToolAPIData = tapToolResponse;
          success = true; 
          console.log("TAP Tool: Data fetched successfully:", tapToolResponse);
        } catch (err) {
          maxRetries--; 
          console.error(`TAP Tool: Error, failed to fetch Tap Tool Data. Retries left: ${maxRetries}`, err);
          await delay(delayTime); 
          delayTime *= 2; 
        }
      }
    }
    if (ldap)  {
      loadTapToolData();
    }
  }, [ldap]);

  React.useEffect(() => {
    const body: any = document.getElementsByTagName('BODY')[0]
    body.className = globalDarkMode ? 'agent-desktop-scroll-dark' : 'agent-desktop-scroll-light'
    return () => {
      body.className = ''
    }
  }, [agentDesktopClass, globalDarkMode])


  React.useEffect(() => {
    vpnConnectionStateHandler.initializeVpnCheckContext(setNetworkMode, setIsValidCountryCode);
    console.log(`VPN_DISCONNECT_CHECK - App Component Mounted - ${pageHistoryPathname} and LDAP is ${ldap}`)
    window.connect.getLog().debug('%s', `VPN_DISCONNECT_CHECK - App Component Mounted - ${pageHistoryPathname} and LDAP is ${ldap}`)

    return () => {
      console.log(`VPN_DISCONNECT_CHECK - App Component UnMounted - ${pageHistoryPathname} and LDAP is ${ldap}`)
      window.connect.getLog().debug('%s', `VPN_DISCONNECT_CHECK - App Component UnMounted - ${pageHistoryPathname} and LDAP is ${ldap}`)
      vpnConnectionStateHandler.clearVpnCheckContext();
    }
  }, [pageHistoryPathname, ldap])

  React.useEffect(() => {
    const isQueueEnabled = enabledQueuesRTS?.includes(currentQueue)
    const isRTCSEnabled = isRTCSEnabledByContactId(enableRTS[currContactId]);
    const contactLevelSentiment = isRTCSContactSentimentCreatedByContactId(contactRTSentiments[currContactId] || conversationLegSentiments[currContactId]);
    const botLevelSentiment = isRTCSBOTContactSentimentCreatedByContactId(conversationLegSentiments[botContactId]);

    updateRTCSEligibilityFlag(isQueueEnabled ? isRTCSEnabled : 'NA');
    updateRTCSContactSentiment(contactLevelSentiment);
    updateRTCSBotContactSentiment(botLevelSentiment);
  }, [enabledQueuesRTS, enableRTS[currContactId], contactRTSentiments[currContactId]?.sentiment, conversationLegSentiments[botContactId], JSON.stringify(conversationLegSentiments)])

  React.useEffect(() => {
    const fetchFeatureFlag = async () => {
      try {
        const response: any = await fetchAgentFeatureFlag(ldap);
        const { data: { json: { User: { Tags = {}, SiteLocation = '' } = {} } = {} } = {} } = response
        dispatch(setAgentDetails({ ...agentDetails, agentSiteLocation: SiteLocation }))
        const agentFeatureTags = Tags;
        dispatch(setAgentFeatureTags(agentFeatureTags))
        console.log("Agent level feature flag = ", agentFeatureTags);
        getReleaseNotesAndSetLocalStorage();
        dispatch(checkMockOfferIdEligibility());
      }
      catch (error) {
        console.log(error);
      }
    }
    if (ldap)
      fetchFeatureFlag()
  }, [ldap])

  React.useEffect(() => {
    const fetchFeatureFlags = async () => {
      try {
        const response = await getOACFeatureFlags(queueACViewOrChatView, ldap);
        dispatch({ type: 'SET_OAC_FEATURE_FLAGS', payload: response });
      } catch (err) {
        console.log(err);
        dispatch({ type: 'SET_OAC_FEATURE_FLAGS', payload: [] });
      }
    }

    fetchFeatureFlags();
  }, [ldap, queueACViewOrChatView, dispatch]);

  const getReleaseNotesAndSetLocalStorage = async () => {
    try {
      const notesResponse: any = await getReleaseNotes();
      if (notesResponse?.data?.success && notesResponse?.data?.data?.length) {
        //fetch o th index as response is already sorted
        const releaseNote = notesResponse.data.data[0];
        dispatch(setReleaseNotes(releaseNote))
        const currentVersion = localStorage.getItem("RELEASE_VERSION")
        if (!(currentVersion && currentVersion === releaseNote.version)) {
          localStorage.setItem("RELEASE_VERSION", releaseNote.version);
          releaseNotePopUp(true)
        }
      }
    } catch (e) {
      console.log("Error Occured in Release Notes")
    }

  }

  React.useEffect(() => {
    if (isVpnConnected && lastAgentStatusWhenVpnIsOn && CCPStatus.ccpState === "PC Recovery") {
      console.log(`VPN_DISCONNECT_CHECK - VPN reconnected and setting status set to ${lastAgentStatusWhenVpnIsOn} from ${CCPStatus.ccpState}`)
      ContactCenter.setStatus(lastAgentStatusWhenVpnIsOn)
    }
    if (!isVpnConnected ) {
      console.log(`VPN_DISCONNECT_CHECK -( VPN Event ) As there is no Active contact, setting status to PC Recovery due to VPN Disconnect`)
      ContactCenter.setStatus("PC Recovery")
    }
    setIsOpen(!isVpnConnected);
  }, [isVpnConnected])

  React.useEffect(() => {
    console.log(`VPN_DISCONNECT_CHECK -State Value Received as ${CCPStatus.ccpState} & VPN Status is ${isVpnConnected}`)
    if (isVpnConnected === true && !AGENT_STATE_VALUES.includes(CCPStatus.ccpState)) {
      console.log(`VPN_DISCONNECT_CHECK - redux status value set to ${CCPStatus.ccpState}`)
      dispatch(updateLastAgentStatusOnVpn(CCPStatus.ccpState))
    }
  }, [CCPStatus.ccpState])

    const dialogDismiss = (action: any, data: any) => {
    return dispatch({ type: action, payload: data })
  }

  const handleAuthFail = () => {
    dispatch(setAuthFail(false))
  }

  return (
    <V3Provider theme={V3Theme} colorScheme='light'>
      <Provider theme={V2Theme}>
        {!CCPStatus.isInit && showCCPLoader.indexOf(location.pathname) >= 0 && (
          <div className='ccp-loader'>
            {' '}
            <ProgressCircle aria-label='Loading…' isIndeterminate />{' '}
          </div>
        )}
        {showLoaderOnOAC && (
          <div className='ccp-loader'>
            {' '}
            <ProgressCircle aria-label='Loading…' isIndeterminate />{' '}
          </div>
        )}
        <Router>

          <div className={agentDesktopClass}>
            <div id='ccpContainer' style={{ display: 'none' }} />
            <SecureRoute path='/(!?chat|call|history)' component={NavigationMenu} />
            <main>
              <VpnValidation>
                <Route path='/' exact component={Login} />
                <Route path='/login/callback' exact component={LoginCallback} />
                <SecureRoute path='/chat' component={Chat} />
                <SecureRoute path='/call' component={Phone} />
                <SecureRoute path='/profile' component={Profile} />
                <SecureRoute path='/history' component={AllConversations} />
                <Route path='/logout' component={Logout} />
              </VpnValidation>
            </main>
          </div>

          {notification.show && <Notification notification={notification} currentConversation={currentConversation} language={language} />}
          <DialogContainer onDismiss={() => dialogDismiss("SETOFFLINEINDICATOR", { isOpen: false })} type='modal'>
            {alertData?.isOpen && <Offline language={language} alertData={alertData} />}
          </DialogContainer>
          <DialogContainer onDismiss={() => dialogDismiss("INCOMING_CALL", { openPopup: "no" })} type='modal'>
            {incomingCallData?.openPopup === "yes" && <IncomingCall darkMode={globalDarkMode} incomingCallData={incomingCallData} />}
          </DialogContainer>
          {
            participantLeft && <RemoveParticipant lang={language} />
          }
          <DialogContainer onDismiss={() => dialogDismiss("MISSED_CALL", { isMissed: false })} type='modal'>
            {missedCallData?.isMissed && <MissedCall darkMode={globalDarkMode} missedCallData={missedCallData} />}
          </DialogContainer>
          <DialogContainer onDismiss={() => dialogDismiss("RECORDING_DISABLED", { ...recordingDisabled, failed: false })} type='modal'>
            {recordingDisabled.failed && <FailurePopup language={language} darkMode={globalDarkMode} />}
          </DialogContainer>
          {<DialogContainer onDismiss={() => setIsOpen(false)} type='modal'>
            {isOpen && <AlertDialog
              title="VPN_DISCONNECTED OR FAILED_TO_VERIFY"
              variant="error"
              primaryActionLabel="Close">
              {localization[language].NOTIFICATIONS.VPN_DISCONNECTED_MESSAGE}
              {!isValidCountryCode && (
                <Text UNSAFE_className="text-red-500 text-sm font-medium mt-1"
                  UNSAFE_style={{
                    position: 'absolute',
                    bottom: '5rem',
                    left: '10rem',
                    right: '1rem'
                  }}>
                  Country Check Failed!
                </Text>
              )}
            </AlertDialog>}
          </DialogContainer>
          }
          {<DialogContainer onDismiss={() => releaseNotePopUp(false)} type='modal'>
            {releaseNoteOpen && <AlertDialog
              title={localization[language].NOTIFICATIONS.RELEASE_NOTES + ' v'+releaseNoteState.version}
              primaryActionLabel="Close">
              {localization[language].NOTIFICATIONS.WHAT_NEW}
              {releaseNoteState[`releaseNotes_${language}`].length ?
                releaseNoteState[`releaseNotes_${language}`].map((notes) => {
                  return <li>{notes}</li>
                }) : ""}
            </AlertDialog>}
          </DialogContainer>
          }
          <DialogContainer onDismiss={() => handleAuthFail()} type='modal'>
            {isAuthFail && <AlertDialog
              title={localization[language].NOTIFICATIONS.AUTHENTICATION_ERROR_TITLE}
              variant="error"
              primaryActionLabel="OK">
              {localization[language].NOTIFICATIONS.AUTHENTICATION_ERROR_BODY}
            </AlertDialog>}
          </DialogContainer>
          {!notification.show && networkMode === 'Offline' && <NetworkNotification language={language} networkMode={networkMode} setNetworkMode={() => setNetworkMode('')} />}
        </Router>
      </Provider>
    </V3Provider>
  )
}

const oktaAuth = new OktaAuth(oktaconfig.oidc)

const App = () => {
  const history = useHistory()
  const restoreOriginalUri = async (_oktaAuth: any, originalUri: string) => {
    history.replace(toRelativeUrl(originalUri, window.location.origin))
    window.location.reload()
  }
  useOnLoad(oktaAuth)
  return (
    <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
      <ReduxProvider store={store}>
        <ThemeContainer />
      </ReduxProvider>
    </Security>
  )
}

export default App